import React, { useRef, useState } from "react";
import { Camera, Upload, X, Loader2, CheckCircle2, Sparkles, Trash2, Edit3 } from "lucide-react";
import api, { CATEGORIES } from "../lib/api";
import { toast } from "sonner";

export default function PhotoMenuUpload({ onImported, onCategoriesChanged, dailyMode = false }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);
  const [suggestedCats, setSuggestedCats] = useState([]);
  const [importing, setImporting] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Solo immagini"); return; }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
    setItems([]);
  };

  const scan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/menu/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.items.length) { toast.error("Nessun piatto rilevato nella foto"); return; }
      setSuggestedCats(data.suggested_categories || []);
      setItems(data.items.map((i) => ({ ...i, _selected: true })));
      const msg = data.suggested_categories?.length ? `${data.count} piatti · ${data.suggested_categories.length} nuove categorie` : `Rilevati ${data.count} piatti`;
      toast.success(msg);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore scansione foto"); }
    finally { setScanning(false); }
  };

  const importSelected = async () => {
    const toImport = items.filter((i) => i._selected).map(({ _selected, ...rest }) => ({ ...rest, is_daily_menu: dailyMode }));
    if (toImport.length === 0) { toast.error("Seleziona almeno un piatto"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/menu/import", { items: toImport });
      toast.success(dailyMode ? `${data.count} piatti aggiunti al Fuori Menu 🔥` : `${data.count} piatti aggiunti al menu`);
      setItems([]); setSuggestedCats([]); setFile(null); setPreview(null);
      if (inputRef.current) inputRef.current.value = "";
      onImported?.(data.created);
      onCategoriesChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore import"); }
    finally { setImporting(false); }
  };

  const updateItem = (idx, patch) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const selectedCount = items.filter((i) => i._selected).length;

  return (
    <div className="bg-white rounded-3xl border border-[#E8E2D5] p-5">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D99B26] to-[#C85A32] grid place-items-center text-white"><Camera size={16}/></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-bold">Metodo alternativo</div>
          <h3 className="font-serif-display text-xl font-bold leading-tight">Carica foto del menu</h3>
        </div>
      </div>
      <p className="text-xs text-[#6B625B] mt-2">Fotografa il tuo menu cartaceo, la AI estrae automaticamente nomi, prezzi e categorie.</p>

      {!preview && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="photo-upload-btn" onClick={() => inputRef.current?.click()} className="rounded-2xl border-2 border-dashed border-[#E8E2D5] hover:border-[#C85A32] bg-[#FBF9F5] py-4 flex flex-col items-center gap-1 transition">
            <Upload size={20} className="text-[#C85A32]"/><span className="text-xs font-bold">Carica foto</span>
          </button>
          <button data-testid="photo-camera-btn" onClick={() => cameraRef.current?.click()} className="rounded-2xl border-2 border-dashed border-[#E8E2D5] hover:border-[#C85A32] bg-[#FBF9F5] py-4 flex flex-col items-center gap-1 transition">
            <Camera size={20} className="text-[#C85A32]"/><span className="text-xs font-bold">Scatta ora</span>
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])}/>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files?.[0])}/>
        </div>
      )}

      {preview && items.length === 0 && (
        <div className="mt-4 relative">
          <img src={preview} alt="Menu" className="w-full h-56 object-cover rounded-2xl border border-[#E8E2D5]"/>
          <button data-testid="photo-clear-btn" onClick={() => { setFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ""; }} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 grid place-items-center"><X size={14}/></button>
          <button data-testid="photo-scan-btn" onClick={scan} disabled={scanning} className="mt-3 w-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white rounded-full py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {scanning ? <><Loader2 className="animate-spin" size={16}/> Analisi con AI...</> : <><Sparkles size={16}/> Estrai piatti dalla foto</>}
          </button>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-4">
          {suggestedCats.length > 0 && (
            <div data-testid="suggested-cats-box" className="mb-3 rounded-2xl bg-gradient-to-r from-[#FEF3D6] to-[#FDECE4] border-2 border-[#D99B26] p-3">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#8C580C] flex items-center gap-1">✨ Nuove categorie rilevate</div>
              <p className="text-xs text-[#231F1D] mt-1">La AI ha trovato piatti in categorie non ancora presenti nel tuo menu. Verranno create automaticamente all'import:</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {suggestedCats.map((c) => (
                  <span key={c} data-testid={`suggested-cat-${c}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-[#8C580C] text-xs font-black border border-[#F4E3B8]">
                    <Sparkles size={11}/> {c}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold text-[#3B503A]"><CheckCircle2 size={12} className="inline mr-1"/> {selectedCount}/{items.length} selezionati</div>
            <button data-testid="photo-reset-btn" onClick={() => { setItems([]); setPreview(null); setFile(null); }} className="text-xs text-[#6B625B] hover:text-[#231F1D] font-semibold">Ricomincia</button>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {items.map((it, idx) => (
              <div key={idx} data-testid={`scan-item-${idx}`} className={`rounded-2xl border p-3 ${it._selected ? "border-[#3B503A] bg-[#EAF0E9]/40" : "border-[#E8E2D5] bg-[#FBF9F5] opacity-70"}`}>
                <div className="flex items-start gap-2">
                  <input data-testid={`scan-select-${idx}`} type="checkbox" checked={it._selected} onChange={(e) => updateItem(idx, { _selected: e.target.checked })} className="mt-1 accent-[#3B503A]"/>
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-6 gap-2">
                    <input data-testid={`scan-name-${idx}`} value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} className="sm:col-span-3 rounded-lg border border-[#E8E2D5] px-2 py-1 text-sm font-semibold bg-white" placeholder="Nome piatto"/>
                    <input data-testid={`scan-price-${idx}`} type="number" step="0.5" value={it.price} onChange={(e) => updateItem(idx, { price: Number(e.target.value) })} className="sm:col-span-1 rounded-lg border border-[#E8E2D5] px-2 py-1 text-sm bg-white" placeholder="€"/>
                    <select data-testid={`scan-cat-${idx}`} value={it.category} onChange={(e) => updateItem(idx, { category: e.target.value })} className="sm:col-span-2 rounded-lg border border-[#E8E2D5] px-2 py-1 text-sm bg-white">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      {suggestedCats.map((c) => <option key={`sug-${c}`} value={c}>{c} (nuova)</option>)}
                    </select>
                  </div>
                  <button data-testid={`scan-remove-${idx}`} onClick={() => removeItem(idx)} className="w-7 h-7 rounded-full hover:bg-red-100 text-red-500 grid place-items-center flex-shrink-0"><Trash2 size={12}/></button>
                </div>
                {it.description && <div className="mt-1 ml-6 text-xs text-[#6B625B] italic truncate">{it.description}</div>}
                {it.allergens?.length > 0 && <div className="mt-1 ml-6 flex flex-wrap gap-1">{it.allergens.map((a) => <span key={a} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F4E9E3] text-[#C85A32] font-bold">{a}</span>)}</div>}
              </div>
            ))}
          </div>
          <button data-testid="photo-import-btn" onClick={importSelected} disabled={importing || selectedCount === 0} className="mt-3 w-full bg-[#3B503A] hover:bg-[#2f4030] text-white rounded-full py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {importing ? <><Loader2 className="animate-spin" size={16}/> Import...</> : <><Edit3 size={16}/> Aggiungi {selectedCount} piatti al menu</>}
          </button>
        </div>
      )}
    </div>
  );
}
